import React from 'react';
import { Modal, Timeline, Tag, Empty } from 'antd';
import {
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Calendar,
  Package
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface SyncHistoryItem {
  id: string;
  platform: string;
  status: 'success' | 'failed' | 'in_progress';
  startTime: string;
  endTime?: string;
  recordsSynced?: number;
  errorMessage?: string;
}

interface SyncStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  platformName: string;
  syncHistory: SyncHistoryItem[];
  isLoading?: boolean;
}

const SyncStatusModal: React.FC<SyncStatusModalProps> = ({
  isOpen,
  onClose,
  platformName,
  syncHistory,
  isLoading = false
}) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'in_progress':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'success':
        return <Tag color="success">Success</Tag>;
      case 'failed':
        return <Tag color="error">Failed</Tag>;
      case 'in_progress':
        return <Tag color="processing">In Progress</Tag>;
      default:
        return <Tag color="default">Unknown</Tag>;
    }
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    if (!endTime) return 'In progress...';

    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const durationSec = Math.floor(durationMs / 1000);

    if (durationSec < 60) return `${durationSec}s`;
    const minutes = Math.floor(durationSec / 60);
    const seconds = durationSec % 60;
    return `${minutes}m ${seconds}s`;
  };

  const timelineItems = syncHistory.map((item) => ({
    dot: getStatusIcon(item.status),
    children: (
      <div className="pb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {getStatusTag(item.status)}
            <span className="text-xs text-gray-500">
              {dayjs(item.startTime).fromNow()}
            </span>
          </div>
        </div>

        <div className="text-sm text-gray-700">
          <div className="flex items-start gap-1.5">
            <Calendar className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Started: {new Date(item.startTime).toLocaleString()}
              {item.endTime && (
                <span className="ml-4">
                  <Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  Duration: {formatDuration(item.startTime, item.endTime)}
                </span>
              )}
              {item.recordsSynced !== undefined && (
                <span className="ml-4">
                  <Package className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  Records synced: {item.recordsSynced.toLocaleString()}
                </span>
              )}
            </span>
          </div>

          {item.errorMessage && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              <strong>Error:</strong> {item.errorMessage}
            </div>
          )}
        </div>
      </div>
    ),
  }));

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-indigo-600" />
          <span className="text-lg font-semibold">{platformName} Sync History</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
    >
      <div className="mt-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-indigo-600 animate-spin" />
            <span className="ml-2 text-gray-600">Loading sync history...</span>
          </div>
        ) : syncHistory.length === 0 ? (
          <Empty
            description={
              <span className="text-gray-500">
                No sync history available for {platformName}
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Timeline items={timelineItems} />
        )}
      </div>

      {syncHistory.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-gray-600">
            <strong>Note:</strong> Sync history shows the last 10 synchronization attempts.
            Successful syncs ensure your data is up-to-date with {platformName}.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default SyncStatusModal;
